//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import React, { ReactNode, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { useIsElementVisible } from '@hooks/useIsElementVisible';

const intersectionObserverOptions: IntersectionObserverInit = {
  root: null,
  rootMargin: '0px',
  threshold: 0.1, // Trigger when 10% of the target is visible
};

interface InfiniteScrollProps {
  children: ReactNode;
  className?: string;
  isEnabled: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
}

export const InfiniteScroll = ({
  children,
  className,
  isEnabled,
  isLoading,
  onLoadMore,
}: InfiniteScrollProps) => {
  const pageEndRef = useRef<HTMLDivElement>(null);
  const isPageEndVisible = useIsElementVisible(
    pageEndRef,
    isEnabled,
    intersectionObserverOptions,
  );

  useEffect(() => {
    if (!isLoading && isPageEndVisible) {
      onLoadMore();
    }
  }, [onLoadMore, isLoading, isPageEndVisible]);

  return (
    <ContentWrapper className={className}>
      {children}
      <PageEnd ref={pageEndRef}>{isLoading && <CenteredSpinner />}</PageEnd>
    </ContentWrapper>
  );
};

const ContentWrapper = styled.div`
  height: 100%;

  /**
   * scrollbar-gutter is not supported in Safari.
   * Scrollbar will not show up without overflow Safari's optimization.
   */
  overflow-y: scroll;

  @supports (scrollbar-gutter: stable) {
    overflow-y: auto;
    scrollbar-gutter: stable;
  }
`;

const PageEnd = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[1]};
  `,
);
